import { useState } from "react";
import { Navigate, useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useAuth, isStaff } from "../../lib/auth";

const Field = ({ id, label, ...p }) => <div><label className="label" htmlFor={id}>{label}</label><input id={id} className="input" data-testid={`auth-${id}`} {...p} /></div>;

export default function AuthPage() {
  const [sp] = useSearchParams();
  const next = sp.get("next");
  const { user, login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState(next?.startsWith("/register") ? "register" : "login");
  const [f, setF] = useState({ name: "", email: "", password: "", phone: "", code: "" });
  const [mfa, setMfa] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  if (user && !busy) return <Navigate to={next || (isStaff(user) ? "/staff" : "/portal")} replace />;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      if (mode === "forgot") { await api.post("/auth/forgot-password", { email: f.email }); toast.success("If an account exists, a reset link has been emailed."); setMode("login"); return; }
      const r = mode === "login" ? await login(f.email, f.password, f.code) : await register({ name: f.name, email: f.email, password: f.password, phone: f.phone });
      if (r.mfa_required) { setMfa(true); return; }
      nav(next || (isStaff(r.user) ? "/staff" : "/portal"), { replace: true });
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <div className="grid min-h-[80vh] lg:grid-cols-2">
      <div className="relative hidden lg:block"><img src="/images/greece.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" /><div className="absolute inset-0 bg-[#0a2f4d]/50" /><div className="absolute bottom-12 left-12 max-w-md text-white"><h2 className="text-4xl font-semibold">Your journey, all in one place.</h2><p className="mt-2 text-slate-200">Register, pay, upload documents and see your rooming — securely.</p></div></div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-4" data-testid="auth-form">
          <h1 className="text-4xl font-semibold">{mode === "login" ? "Traveler login" : mode === "register" ? "Create your account" : "Reset password"}</h1>
          {next?.startsWith("/register") && <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900" data-testid="auth-continue-note">Sign in or create an account to continue registering for tour <b>{next.split("/")[2]}</b>. Your tour selection is saved.</p>}
          {mode === "register" && <Field id="name" label="Full name" required value={f.name} onChange={set("name")} />}
          <Field id="email" label="Email" type="email" required value={f.email} onChange={set("email")} autoComplete="email" />
          {mode !== "forgot" && <Field id="password" label="Password" type="password" required minLength={mode === "register" ? 8 : 1} value={f.password} onChange={set("password")} autoComplete={mode === "login" ? "current-password" : "new-password"} />}
          {mode === "register" && <Field id="phone" label="Phone (optional)" value={f.phone} onChange={set("phone")} />}
          {mfa && <Field id="code" label="Authenticator code" inputMode="numeric" value={f.code} onChange={set("code")} autoFocus />}
          {err && <p className="rounded-md bg-rose-50 p-3 text-sm text-rose-700" role="alert" data-testid="auth-error">{err}</p>}
          <button className="btn-primary w-full" disabled={busy} data-testid="auth-submit">{busy ? "Please wait…" : mode === "login" ? (mfa ? "Verify & sign in" : "Sign in") : mode === "register" ? "Create account & continue" : "Send reset link"}</button>
          <div className="flex justify-between text-sm">
            {mode !== "login" ? <button type="button" className="underline" onClick={() => setMode("login")} data-testid="auth-to-login">I have an account</button> : <button type="button" className="underline" onClick={() => setMode("register")} data-testid="auth-to-register">Create an account</button>}
            {mode === "login" && <button type="button" className="text-slate-500 underline" onClick={() => setMode("forgot")} data-testid="auth-to-forgot">Forgot password?</button>}
          </div>
        </form>
      </div>
    </div>
  );
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const [pw, setPw] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/reset-password", { token: sp.get("token"), password: pw }); setDone(true); } catch (e2) { toast.error(errMsg(e2)); }
  };
  return (
    <div className="container-x max-w-sm py-24">
      {done ? <p data-testid="reset-done">Password updated. <Link to="/login" className="underline">Sign in</Link></p> : (
        <form onSubmit={submit} className="space-y-4"><h1 className="text-4xl font-semibold">Choose a new password</h1>
          <input type="password" minLength={8} required className="input" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" /><button className="btn-primary w-full" data-testid="reset-submit">Update password</button></form>)}
    </div>
  );
}
